/**
 * Scroll guard + on-screen diagnostic.
 *
 * There are several independent ways this theme can leave the page unable to
 * scroll, and they are all intermittent, so a single fix aimed at one of them is a
 * guess. This module instead detects the *symptom* -- the visitor tried to scroll
 * and the page did not move -- then works out which cause is responsible, repairs
 * the ones that are safely repairable, and (when diagnostics are enabled) says so
 * on screen so the cause can be reported without opening devtools.
 *
 * Known causes it handles:
 *
 * 1. A leaked scroll lock. The theme freezes the page by putting `scroll-lock` on
 *    <html> (`html[scroll-lock] .page-wrapper { overflow: hidden }`), reference
 *    counted in `utilities.js` from a Set of owners: the menu drawer, the search
 *    modal, the quick-add modal. `pruneDisconnectedScrollLockOwners()` only cleans
 *    owners that left the DOM, so an owner that stays put and never reports closing
 *    keeps the page locked until reload.
 *
 * 2. Leftovers from an interrupted view transition. `utilities.js` sets
 *    `content-visibility: hidden` and `view-transition-name` inline on product
 *    cards during a 'product-grid' transition and removes them in a cleanup
 *    callback. If that callback never runs -- and the theme's own comments note
 *    these transitions can "freeze or white-screen the storefront" in Android
 *    WebViews and in-app browsers -- those inline styles persist.
 *
 * Which element actually scrolls differs by viewport: `base.css` only makes
 * `.page-wrapper` the scroller at >=990px; below that the document scrolls. So the
 * scroller is discovered from real scroll events rather than assumed.
 */

const STUCK_CHECK_MS = 220;

let warned = false;
let banner = null;

/** @type {string} What was open at the moment the lock was taken. */
let lockedBy = 'something that was already closed';

/** @type {Element | Document | null} */
let scroller = null;

/** Surfaces that legitimately own a scroll lock. Deliberately NOT
 *  `[aria-expanded="true"]`: that matches FAQ rows and nav submenus, which never
 *  lock scrolling, and including it stopped the guard ever firing. */
const OPEN_SELECTORS = ['dialog[open]', 'details.menu-drawer-container[open]', 'details[open] > .menu-drawer'];

/** @returns {boolean} */
function somethingIsOpen() {
  for (const selector of OPEN_SELECTORS) {
    if (document.querySelector(selector)) return true;
  }
  try {
    for (const el of document.querySelectorAll('[popover]')) {
      if (el.matches(':popover-open')) return true;
    }
  } catch {
    /* :popover-open unsupported; dialogs above still cover it. */
  }
  return false;
}

/** @returns {string} Plain-English name for whatever is open right now. */
function describeOpen() {
  const names = [];
  /** @param {string} selector @param {string} label */
  const check = (selector, label) => {
    try {
      if (document.querySelector(selector)) names.push(label);
    } catch {
      /* selector unsupported in this browser */
    }
  };
  check('details.menu-drawer-container[open]', 'the MENU drawer');
  check('dialog.search-modal__content[open]', 'the SEARCH panel');
  check('.search-modal__content:popover-open', 'the SEARCH panel');
  check('dialog.quick-add-modal[open]', 'the QUICK-ADD popup');
  check('.quick-add-modal:popover-open', 'the QUICK-ADD popup');
  check('cart-drawer[open], .cart-drawer[open]', 'the CART drawer');
  return names.length ? [...new Set(names)].join(' + ') : 'could not be identified';
}

new MutationObserver(() => {
  if (document.documentElement.hasAttribute('scroll-lock')) lockedBy = describeOpen();
}).observe(document.documentElement, { attributes: true, attributeFilter: ['scroll-lock'] });

/**
 * @param {Element | Document} target
 * @returns {number}
 */
function topOf(target) {
  if (target === document || target === document.documentElement || target === document.body) {
    return window.scrollY || document.documentElement.scrollTop || 0;
  }
  return /** @type {Element} */ (target).scrollTop;
}

/**
 * @param {Element | Document} target
 * @returns {boolean} Whether this element has anywhere left to scroll.
 */
function canScroll(target) {
  const el =
    target === document || target === document.documentElement ? document.documentElement : /** @type {Element} */ (target);
  return el.scrollHeight > el.clientHeight + 4;
}

/**
 * Repairs what is safely repairable and returns what it found.
 * @returns {string[]}
 */
function diagnoseAndRepair() {
  const found = [];

  // 1. Stale scroll lock -- only ever released when nothing is genuinely open,
  //    so this can never unlock the page while a real drawer is on screen.
  if (document.documentElement.hasAttribute('scroll-lock') && !somethingIsOpen()) {
    document.documentElement.removeAttribute('scroll-lock');
    found.push(`a stuck scroll lock left by ${lockedBy}`);
  }

  // 2. Leftover inline styles from an interrupted view transition.
  const ghosts = document.querySelectorAll('[style*="content-visibility"], [style*="view-transition-name"]');
  if (ghosts.length) {
    for (const el of ghosts) {
      /** @type {HTMLElement} */ (el).style.removeProperty('content-visibility');
      /** @type {HTMLElement} */ (el).style.removeProperty('view-transition-name');
    }
    found.push(`${ghosts.length} leftover(s) from an unfinished page transition`);
  }

  return found;
}

/**
 * @param {string} message
 */
function showBanner(message) {
  if (!document.body?.dataset || document.body.dataset.scrollDiagnostics !== 'true') return;
  if (banner) banner.remove();

  banner = document.createElement('div');
  banner.setAttribute('role', 'status');
  banner.style.cssText = [
    'position:fixed',
    'inset-inline:12px',
    'inset-block-end:12px',
    'z-index:2147483647',
    'background:#14171a',
    'color:#fff',
    'font:600 13px/1.45 system-ui,sans-serif',
    'padding:12px 14px',
    'border-radius:8px',
    'box-shadow:0 8px 30px rgba(0,0,0,.45)',
    'max-width:520px',
    'margin-inline:auto',
  ].join(';');
  banner.textContent = `Scrolling was stuck: ${message}. It has been unstuck — please screenshot this.`;

  const close = document.createElement('button');
  close.type = 'button';
  close.textContent = 'Dismiss';
  close.style.cssText =
    'margin-inline-start:10px;background:#fff;color:#14171a;border:0;border-radius:4px;padding:4px 8px;font:inherit;cursor:pointer';
  close.addEventListener('click', () => banner?.remove());
  banner.appendChild(close);

  document.body.appendChild(banner);
}

/**
 * The element a scroll gesture would actually move.
 *
 * A wheel event's target is whatever sits under the cursor, which is almost never
 * the scroller -- so testing the target directly reports "nothing to scroll" and
 * the guard never runs. Walk up instead and take the first ancestor with
 * overflowing content. Crucially this does NOT filter on `overflow`, because the
 * frozen state we are hunting for is precisely an overflowing container whose
 * overflow has been forced to `hidden`.
 *
 * @param {EventTarget | null} start
 * @returns {Element | Document}
 */
function findScroller(start) {
  let node = start instanceof Element ? start : null;

  while (node) {
    if (node.scrollHeight > node.clientHeight + 4) return node;
    node = node.parentElement;
  }

  const wrapper = document.querySelector('.page-wrapper');
  if (wrapper && wrapper.scrollHeight > wrapper.clientHeight + 4) return wrapper;

  return document.documentElement;
}

/**
 * Called when the visitor tries to scroll. Records where the scroller was, then
 * checks a moment later whether it actually moved.
 * @param {Event} event
 */
function onScrollAttempt(event) {
  const target = findScroller(event.target);
  scroller = target;

  if (!canScroll(target)) return;
  const before = topOf(target);

  setTimeout(() => {
    if (topOf(target) !== before) return; // it moved; nothing wrong
    if (somethingIsOpen()) return; // legitimately locked behind a drawer

    const found = diagnoseAndRepair();
    if (found.length === 0) return;

    if (!warned) {
      warned = true;
      console.warn(`[GripSway] Scrolling was stuck. Cause: ${found.join('; ')}. Repaired automatically.`);
    }
    showBanner(found.join('; '));
  }, STUCK_CHECK_MS);
}

document.addEventListener('wheel', onScrollAttempt, { passive: true, capture: true });
document.addEventListener('touchmove', onScrollAttempt, { passive: true, capture: true });
document.addEventListener(
  'keydown',
  (event) => {
    if (['ArrowDown', 'ArrowUp', 'PageDown', 'PageUp', 'Home', 'End', ' '].includes(event.key)) {
      onScrollAttempt(event);
    }
  },
  { passive: true, capture: true }
);
